import React, { useState, useEffect } from 'react';
import gibletIcon from "@/assets/giblet-construct-icon.png";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { ScrollArea } from "@/components/ui/scroll-area";
import { HardHat, Truck, FileText, Users, AlertTriangle, Building2, CheckCircle2 } from 'lucide-react';
import { Link } from "react-router-dom";
import DetailedResponsePanel from './DetailedResponsePanel';
interface DemoCommand {
  id: string;
  query: string;
  processingMessage: string;
  response: string;
  icon: React.ComponentType<any>;
  category: string;
}
interface DetailedResponse {
  title: string;
  content: string;
}
const constructionCommands: DemoCommand[] = [{
  id: 'omb-development',
  query: "CfS: Develop an updated, AI-consumable and blockchain-protected version of the Owners Modularization Blueprint (OMB) tailored for 2025 construction realities.",
  processingMessage: "Analyzing modularization requirements...",
  response: "✓ OMB-2025 framework proposal generated",
  icon: HardHat,
  category: "Strategic Development"
}, {
  id: 'project-status',
  query: "CfK: Expand the Digital Twin Integration Layer of OMB-2025 for AI-Driven Safety and Logistics Simulation",
  processingMessage: "Processing Call for Knowledge request...",
  response: "📋 CfK created for Digital Twin Integration Layer expansion",
  icon: Building2,
  category: "Call for Knowledge (CfK)"
}, {
  id: 'resource-management',
  query: "CfD: Retire Legacy Module Size Standards and Transport Templates from OMB-2013 in favor of Adaptive Modular Standards",
  processingMessage: "Processing Call for Destruction request...",
  response: "🧨 CfD created for retiring legacy OMB-2013 standards",
  icon: Truck,
  category: "Call for Destruction (CfD)"
}, {
  id: 'safety-compliance',
  query: "CfS: Create a CfS to update the Owners Safety Blueprint with a $10,000 Solution Bounty",
  processingMessage: "Processing Call for Solution request...",
  response: "💰 CfS created with $10,000 bounty for Safety Blueprint update",
  icon: AlertTriangle,
  category: "Call for Solution (CfS)"
}, {
  id: 'team-coordination',
  query: "CfK: What proven methods or AI tools have been used to monitor, predict, or mitigate safety risks during modularized construction, particularly within fabrication yards, logistics, or on-site module integration phases?",
  processingMessage: "Processing Call for Knowledge request...",
  response: "CfK created for AI-Augmented Safety Protocols",
  icon: Users,
  category: "Call for Knowledge (CfK)"
}, {
  id: 'document-management',
  query: "CfD: Remove Obsolete Onsite EHSS Practices Misaligned with Modularization Realities",
  processingMessage: "Processing Call for Destruction request...",
  response: "🧨 CfD created for removing obsolete onsite EHSS practices",
  icon: FileText,
  category: "Call for Destruction (CfD)"
}];
const detailedResponses: Record<string, DetailedResponse> = {
  'project-status': {
    title: "Call for Knowledge (CfK)",
    content: `Purpose

This Call for Knowledge (CfK) aims to expand the Digital Twin component of the Owners Modularization Blueprint 2025 (OMB-2025) by integrating advanced use cases for AI-driven safety simulation and logistics optimization. The updated KBs will better support field operations, risk modeling, environmental compliance, and digital factory planning within the iSPAI ecosystem.

📌 Target Objectives

Enhance the Execution Planning with AWP & Digital Twins category

Refine EHSS Planning and Smart Logistics modules

Extend AI agent functionality (Giblet v2 & v3) with predictive modeling capabilities

Improve simulation fidelity for:

Fabrication yard productivity

Transport risks

Emergency response

Hazard detection and preemptive mitigation

Knowledge Base Expansion Scope

Refinement of existing KBs and the development of new sub-KBs:

Existing KBs to Expand:

Execution Planning with AWP & Digital Twins

EHSS and Workforce Planning

Smart Logistics & Transportation

New Sub-KBs Proposed:

"Digital Twin Risk Playbooks" for real-time hazard simulation

"Multi-modal Logistics Delay Predictors" using historical and AI-inferred data

"AI-Enhanced Permit-to-Work Systems" with embedded safety decision trees

Integration Strategy

Ontology Extension: Define new AI-readable metadata fields (e.g., real-time sensory inputs, asset criticality rankings).

Interoperability Layer: Enable integration with BIM, SCADA, and ERP systems for live feed simulation.

Simulation Template Libraries: Codify scenario playbooks for safety inspections, EHSS alerts, and module transport failures.

🤖 AI Agent Support (Giblet Enhancements)

Giblet v2: Enhanced risk advisory services for site leads and planners

Giblet v3: Cross-project analysis identifying recurring EHSS risks and optimal logistics routes

Compliance Alignment

Aligns with:

OSHA Process Safety Management (PSM)

ISO 45001: Occupational Health and Safety

DOT/FMC logistics compliance

🧩 Contributor Roles Needed

Creators: Digital Twin engineers, safety modelers, AWP specialists

Contributors: EPC logistics teams, OEM safety engineers

Confirmers: Certified safety professionals, AI model auditors

Composers: Integration architects to fold KBs into OMB-2025

🪙 Incentive & IP Strategy

Milestone-based token rewards (Gib tokens)

NFT-KB licensing based on channel type:

Private: EPC/OEM safety IP retained

Semi-Private: Shared use under bounded commercial rights

Public: Creator-attributed, royalty-enabled

🧭 Next Steps

Activate CfK across iSPAI's Semi-Private and Public Channels

Appoint safety and logistics SMEs as Confirmers

Define Git-style KB branching and merging protocol`
  },
  'resource-management': {
    title: "Call for Destruction (CfD)",
    content: `🧨 Problem Statement

Legacy modularization size classifications and transport assumptions embedded in the original 2013 Owners Blueprint no longer reflect:

Today's fabrication capabilities

Advanced logistics tech

Evolving environmental/permitting constraints

New assembly methodologies (e.g., pancake and hybrid stacking)

Failure to remove these outdated standards risks:

Suboptimal module planning

Redundant engineering scope

AI model misalignment with current best practices

Targets for Destruction

Obsolete Knowledge Artifacts:

2013 Standard Envelope Definitions for Modules

Generic Truck & Barge Route Templates

Flat fabrication yard productivity assumptions

Redundant Diagrams:

Old plot plan overlays not supporting digital twin adaptability

Manual logistics schedules lacking geospatial AI inputs

Irrelevant Contexts:

Labor availability assumptions based on 2010 workforce dynamics

Permitting flowcharts pre-dating ESG and carbon compliance frameworks

📜 Destruction Protocol

Destruction will occur through the iSPAI CfD governance framework:

Call for Destruction issued: Obsolete KBs tagged

Confirmers engaged: Senior modularization engineers, transport specialists

Consensus Voting: Conducted by NFT-KB token holders

Destruction Triggered: Metadata deprecated, KB retired from Giblet ingestion models

⚠️ Risk Mitigation

All deprecated content to be archived with metadata tags (for historical traceability)

Contributors to be notified with optional CfK invites to re-contribute updated content

AI guardrails updated to suppress obsolete logic in current and future simulations

Benefits of Destruction

Metric | Before CfD | After CfD
---|---|---
AI planning accuracy | ~65% | ↑ >85%
Time to optimal logistics plan | ~6 weeks | ↓ 50%
Rework on transport strategy | High | ↓ 70%
Cross-project reuse | Limited | ↑ by 3x

Recommended Actions

Appoint CfD Confirmers from CURT Modularization Committee

Notify NFT-KB creators of proposed destruction

Use iSPAI's smart contract arbitration for any dispute over IP relevance

Begin sunset protocol with archive backup and flagging for replacement KBs`
  },
  'safety-compliance': {
    title: "Call for Solution (CfS): Update to CURT Owners' Safety Blueprint",
    content: `Channel Type: Semi-Private (CURT Member Companies + iSPAI Verified Contributors)
Status: Open
Bounty: $10,000 USD
Deadline: November 30, 2025
Knowledge Domains: Capital Projects, Safety, Offsite Construction, Modularization, EHSS, AI-in-Construction

🧩 Problem Statement

CURT's Owners' Blueprint for Modularization provides valuable guidance on execution strategies, procurement, and engineering considerations, but the Safety (EHSS) domain remains underdeveloped relative to its operational impact. With rising modularization and offsite fabrication adoption across projects, there is a growing need to rethink and update the Owners' Safety Blueprint to address the evolving risks, opportunities, and governance challenges that come with these new delivery models.

This CfS seeks a multi-domain solution that integrates traditional EHSS protocols with emerging risk scenarios in modular yards, transportation logistics, digital twin safety simulations, and AI-enabled jobsite monitoring.

📚 Required Knowledge Bases (KBs)

Offsite Safety Protocols

Safety workflows within MFYs (Modular Fabrication Yards)

Lessons learned from project case studies (e.g. Chemours-Koch Project "Chill")

Risk Mitigation in Modular Integration

Logistics and transportation hazards

Assembly sequencing and interface risk analysis

EHSS Planning in Modular Execution Strategies

Comparative analysis: stick-built vs modularized project safety

Updates aligned with FEL1-FEL3 stages

Smart Jobsite / AI-Augmented Safety Monitoring

Role of digital twins and computer vision for real-time risk detection

AI-powered predictive safety modeling

IP and Governance Implications for Shared Safety Standards

Licensing models for reusable safety KBs across multiple CURT members

Attribution rules for multi-party contributions to evolving safety protocols

Desired Outputs

Composed Solution (KB Assembly): A finalized "Safety Blueprint for Modular Execution" that integrates all validated KBs into a single AI-consumable asset.

Format: Solution must be modular (i.e. usable by both human SMEs and AI agents like Giblet Construct) and support grounding for CURT's AI agents.

Compliance: All contributions must follow iSPAI's IP governance and tokenized attribution model.

Evaluation Criteria

Relevance and alignment with CURT's current EHSS, Modularization, and Productivity priorities

Technical rigor and grounding in real-world project data

Completeness of cross-channel applicability (MFY, onsite, digital twin)

AI-ingestibility and structuring for Giblet validation

Inclusion of sustainability and regulatory trends impacting EHSS

🧬 Attribution and Compensation Model

$10,000 Total Bounty to be distributed among Curators, Contributors, Confirmers, and the Composer based on final IDEAS ratings and Confirmed KB utilization.

Royalties will be embedded for reuse within future CURT initiatives and cross-enterprise pilots via NFT-KBs under CURT's semi-private licensing strategy.

Attribution will be permanently embedded via iSPAI's blockchain-based contribution ledger.

📅 Key Dates

CfS Launch: September 29, 2025

KB Contributions Due: November 15, 2025

Final Composition Review: November 30, 2025

Bounty Distribution: December 10, 2025

🧑‍💼 Who Should Contribute?

CURT Member Company Safety Leaders

Modularization SMEs with FEL1–FEL3 experience

Confirmers with EHSS compliance and regulatory expertise

AI + Construction technologists building jobsite automation tools`
  },
  'team-coordination': {
    title: "Call for Knowledge (CfK): Integrating AI-Powered Safety Monitoring in Modularized Construction Projects",
    content: `📋 CALL FOR KNOWLEDGE (CfK)
Integrating AI-Powered Safety Monitoring in Modularized Construction Projects

Channel Type: Semi-Private (CURT Safety + Modularization Contributors)
Status: Open
Associated CfS: Update to CURT Owners' Safety Blueprint
Response Type: Curated Knowledge Base Contribution (KB: AI-Augmented Safety Protocols)
Deadline for Initial Submissions: October 25, 2025
Target Roles: Contributors, Confirmers

KB TITLE: AI-Augmented Safety Protocols for Modular Execution

Contributor: Verified Safety Director (CURT Member)

SUMMARY

AI integration into modular construction safety workflows is gaining traction, especially in high-risk environments like MFYs and during module transport.

KEY INPUTS

Computer Vision Systems:

Used in modular yards to detect unsafe behaviors such as PPE violations, trip hazards, and fall risks in real-time.

Example: Integration of SmartVid.io at a Gulf Coast MFY led to a 32% reduction in near misses over 6 months.

Digital Twin Integration:

Simulated jobsite conditions allowed for pre-task planning and risk identification before module arrival.

Example: Chevron pilot in 2024 used Bentley's OpenTwin platform to simulate crane lifts and module set sequencing, reducing risk exposure by 18%.

Predictive Analytics for Workforce Risk Profiling:

Historical safety data linked to specific crafts and sequencing was analyzed to predict which work packages were most likely to incur EHSS violations.

Example: Koch Modular used this during Project "Chill" to assign higher-rated safety personnel to congested work areas.

✅ REQUIRED CONFIRMATIONS

• Validation of reduction in EHSS incidents
• Documentation of AI tools and operational context
• Cross-referencing against regulatory compliance frameworks (OSHA, ISO 45001)

🏗️ MODULAR FABRICATION YARD (MFY) APPLICATIONS

Real-Time Safety Monitoring:

Computer vision systems monitor workers for:
• PPE compliance (hard hats, safety glasses, steel-toed boots)
• Fall protection system usage
• Proper lifting techniques and ergonomic practices
• Equipment operation safety protocols
• Fire safety and emergency evacuation compliance

Predictive Risk Assessment:

AI algorithms analyze:
• Historical accident data patterns
• Weather conditions and their impact on operations
• Equipment maintenance schedules and failure prediction
• Workforce fatigue levels and rotation schedules
• Material handling risks and optimal routing

Automated Safety Documentation:

Digital systems provide:
• Real-time safety incident reporting
• Automated compliance tracking for regulatory inspections
• Digital safety training verification and competency tracking
• Integration with permit-to-work systems
• Continuous safety performance metrics and KPI dashboards

🚛 LOGISTICS AND TRANSPORTATION SAFETY

Route Optimization with Safety Integration:

AI-powered systems consider:
• Bridge clearances and weight restrictions
• Weather-related transport risks
• Traffic patterns and peak congestion periods
• Emergency response accessibility along routes
• Specialized escort and pilot car requirements

Real-Time Monitoring During Transport:

Technologies include:
• GPS tracking with geofencing alerts
• Load monitoring sensors for shift detection
• Weather monitoring and rerouting capabilities
• Communication systems between transport teams
• Emergency response protocol automation

Pre-Transport Safety Validation:

Automated checks verify:
• Module securing and tie-down compliance
• Transport vehicle inspection completion
• Route planning and permit validation
• Emergency contact and response plan activation
• Insurance and liability coverage confirmation

🔗 ON-SITE MODULE INTEGRATION SAFETY

Crane and Lifting Operations:

AI-enhanced systems monitor:
• Crane capacity vs. load calculations
• Wind speed and weather condition impacts
• Operator certification and fatigue monitoring
• Ground conditions and stability assessments
• Proximity detection for personnel and equipment

Interface and Connection Safety:

Digital tools support:
• Precision alignment monitoring to prevent forced connections
• Utility connection safety protocols and lockout/tagout procedures
• Structural integrity verification before load transfer
• Quality control checkpoints with digital sign-off requirements
• Real-time coordination between multiple craft disciplines

Site Integration Risk Management:

Comprehensive monitoring includes:
• Personnel tracking and proximity alerts in high-risk zones
• Environmental monitoring (noise, air quality, vibration)
• Emergency response system integration and testing
• Multi-trade coordination and conflict resolution
• Progress tracking with safety milestone validation

PERFORMANCE METRICS AND VALIDATION

Safety Performance Indicators:

Key metrics tracked include:
• Incident rate reduction (target: 40-60% improvement)
• Near-miss reporting and resolution time
• Safety training completion and competency scores
• Regulatory compliance audit results
• Workers' compensation claims reduction

Technology Adoption Metrics:

Implementation success measured by:
• System uptime and reliability percentages
• User adoption rates across different craft disciplines
• ROI calculation including avoided incidents and insurance benefits
• Integration success with existing safety management systems
• Scalability across multiple project sites and phases

Regulatory Compliance Tracking:

Continuous monitoring ensures:
• OSHA compliance scoring and audit readiness
• ISO 45001 certification maintenance
• Local jurisdiction requirement adherence
• Environmental impact assessment compliance
• Insurance carrier requirement satisfaction

GOVERNANCE AND IP PROTECTION

Knowledge Base Attribution:

Clear documentation of:
• Contributing organization credits and recognition
• Intellectual property ownership and licensing terms
• Shared learning protocols and cross-project application
• Confidentiality requirements for proprietary methodologies
• Revenue sharing models for commercially viable innovations

Quality Assurance Framework:

Rigorous validation through:
• Peer review by certified safety professionals
• Third-party auditing and verification processes
• Pilot project implementation and results documentation
• Continuous improvement feedback loops
• Integration with industry best practice databases

CONTRIBUTION REQUIREMENTS

Technical Documentation:

Contributors must provide:
• Detailed implementation methodologies and step-by-step procedures
• Quantified safety performance improvements with baseline comparisons
• Technology specifications and integration requirements
• Cost-benefit analysis including implementation and operational costs
• Lessons learned and recommendations for future applications

Validation Evidence:

Required supporting materials:
• Project case studies with verified outcomes
• Regulatory compliance certifications and audit results
• Stakeholder testimonials and adoption feedback
• Performance data and statistical analysis
• Scalability assessments and replication guidelines

🚀 NEXT STEPS

Immediate Actions:

• Submit initial knowledge contributions by October 25, 2025
• Engage Confirmers for peer review and validation
• Schedule pilot project implementations for testing
• Develop integration protocols with existing safety systems
• Establish performance measurement and reporting frameworks

Long-term Integration:

• Incorporate validated methodologies into CURT Safety Blueprint update
• Create standardized implementation guides for CURT member companies
• Develop training curricula for safety professionals and AI technologists
• Establish ongoing knowledge sharing and improvement processes
• Scale successful implementations across multiple project types

This CfK represents a critical step in advancing the integration of AI technologies with traditional safety management practices, ultimately leading to safer, more efficient modularized construction projects across the industry.`
  },
  'document-management': {
    title: "Call for Destruction (CfD): Remove Obsolete Onsite EHSS Practices Misaligned with Modularization Realities",
    content: `🧨 CALL FOR DESTRUCTION (CfD)
Remove Obsolete Onsite EHSS Practices Misaligned with Modularization Realities

Channel Type: Semi-Private (CURT Safety + iSPAI Confirmers)
Status: Open
Associated CfS: Update to CURT Owners' Safety Blueprint
Response Type: Justification for Removal or Deprecation
Deadline for Community Vote: October 31, 2025
Target Roles: Confirmers, Creators

PROMPT

"Which safety assumptions or procedures currently embedded in the CURT Safety Blueprint are no longer valid or applicable due to the shift toward modularization, and should be deprecated?"

Contributors are invited to submit flagged content or practices for review, with justification and evidence of obsolescence. Confirmers will validate whether these legacy practices hinder modern project execution or contradict current regulatory standards.

📋 DETAILED RESPONSE (INITIAL SUBMISSION EXAMPLE)

Flagged Content for Removal:

"Onsite scaffolding plans must be completed during FEL1 phase and updated monthly through construction."

REASON FOR DESTRUCTION

Obsolete Paradigm:

This practice was developed under stick-built paradigms and does not reflect the minimized scaffolding needs in MFY and modular assembly workflows.

Modern Reality:

In modularized environments, scaffolding is engineered into the module assembly strategy, and much of the work occurs at-grade or in flat-pack configurations.

⚠️ RISKS OF RETAINING OBSOLETE PRACTICE

Economic Impact:
• Redundant planning cycles increase engineering effort and cost
• Misalignment during FEL stages can introduce scope creep and delays
• Unnecessary materials procurement for non-applicable scaffolding systems

Operational Inefficiencies:
• Engineering teams waste time on irrelevant planning activities
• Project schedules become artificially extended due to unnecessary milestones
• Resource allocation conflicts between traditional and modular approaches

Safety Implications:
• Outdated scaffolding assumptions may not address modular-specific risks
• Disconnect between MFY safety protocols and onsite safety planning
• Potential for conflicting safety procedures between fabrication and assembly phases

🔄 PROPOSED REPLACEMENT

Updated Safety Practice:

"Scaffolding plans should be integrated into the Module Execution Strategy and coordinated with the module fabricator's pancake assembly methods during FEL2 planning. Onsite scaffolding plans apply only to module interface zones."

Key Improvements:
• Aligns scaffolding planning with modular execution timelines
• Focuses onsite scaffolding efforts on actual need areas (interfaces, connections)
• Integrates with fabricator's established safety protocols
• Reduces redundant planning while maintaining safety standards

✅ VERIFICATION REQUEST TO CONFIRMERS

Required Evidence:
• Evidence from project case studies (e.g., Koch Modular, Chevron projects)
• Alignment with modular yard safety protocols
• FEL planning impact data from CURT's Offsite Sprint Team materials
• Regulatory compliance verification for modular-specific approaches

Validation Criteria:
• Confirmation that traditional scaffolding planning creates inefficiencies
• Evidence that modular approaches provide equivalent or superior safety outcomes
• Documentation of cost and schedule impacts from obsolete practices
• Regulatory body acceptance of modular-specific safety protocols

🏗️ ADDITIONAL FLAGGED PRACTICES FOR REVIEW

1. Traditional Fall Protection Planning:

*Current Practice:* "Personal fall protection systems must be designed for each work area during initial site planning."

*Obsolescence Issue:* Most modular work occurs at fabrication yard ground level; onsite work is primarily connection and interface activities.

*Proposed Update:* "Fall protection systems should differentiate between MFY protocols (ground-level assembly) and onsite protocols (module interface and connection work)."

2. Sequential Trade Safety Coordination:

*Current Practice:* "Safety meetings must include all trades working in adjacent areas on the same day."

*Obsolescence Issue:* Modular execution often has fewer concurrent trades onsite, with most coordination happening during pre-planned module delivery sequences.

*Proposed Update:* "Safety coordination should focus on module delivery, crane operations, and interface work, with MFY safety protocols handled separately through fabricator's systems."

3. Material Handling Safety Protocols:

*Current Practice:* "All materials must be inspected and approved upon delivery to jobsite."

*Obsolescence Issue:* Modules arrive as completed assemblies with materials already incorporated and inspected at fabrication facilities.

*Proposed Update:* "Material inspections should focus on module integrity, transport-related damage, and interface materials, while relying on fabricator's QA/QC for internal module components."

IMPACT ANALYSIS

Benefits of Destruction:

| Metric | Current State | Post-CfD Target |
|--------|---------------|-----------------|
| FEL1 Planning Time | 6-8 weeks | ↓ 40% (3-5 weeks) |
| Scaffolding Cost Variance | 25-40% over budget | ↓ 60% reduction in variance |
| Safety Planning Accuracy | 65% applicable to modular | ↑ 90% modular-relevant practices |
| Cross-project Reusability | Limited | ↑ 3x improvement |

Risk Mitigation:
• Archived practices remain accessible for stick-built projects
• Transition period allows gradual adoption of new practices
• Regular review cycles ensure new practices remain current
• Integration with existing regulatory compliance frameworks

🗳️ COMMUNITY VOTING PROCESS

Voting Eligibility:
• CURT member company safety directors
• iSPAI verified Confirmers with modular experience
• NFT-KB token holders in safety domain
• Certified safety professionals with modular project experience

Voting Criteria:
• Evidence of practice obsolescence
• Impact on project efficiency and cost
• Safety outcome equivalency or improvement
• Regulatory compliance maintenance

Consensus Requirements:
• 66% majority vote for destruction approval
• No vetoes from regulatory compliance Confirmers
• Validation from at least 3 modular project case studies
• Approval from CURT Safety Committee leadership

SUMMARY AND NEXT STEPS

| Call Type | Objective | Action Required | Reward Alignment |
|-----------|-----------|----------------|------------------|
| CfS | Update the CURT Owners' Safety Blueprint | Submit composed solution (multi-KB) | $10,000 bounty + royalties |
| CfK | Provide validated knowledge on AI-enabled safety practices | Submit KB contribution and pass confirmation | Tokenized attribution |
| CfD | Flag and remove obsolete safety practices | Justify removal + vote for destruction | Confirmer validation reward |

🚀 IMMEDIATE ACTIONS

For Contributors:
• Submit additional flagged practices by October 15, 2025
• Provide supporting evidence and case study data
• Engage with Confirmers for preliminary validation
• Prepare detailed justifications for community review

For Confirmers:
• Review submitted flagged practices for validity
• Validate evidence and supporting documentation
• Participate in community voting process
• Ensure regulatory compliance during transition

For the Community:
• Participate in voting process for submitted CfDs
• Provide feedback on proposed replacement practices
• Share experiences from modular project implementations
• Support transition to updated safety protocols

This CfD represents a critical step in modernizing safety practices to align with the realities of modular construction while maintaining the highest standards of worker protection and regulatory compliance.`
  },
  'omb-development': {
    title: "Owners Modularization Blueprint 2025 (OMB-2025)",
    content: `To fulfill the Call for Solutions (CfS) to develop an AI-consumable, blockchain-protected Owners Modularization Blueprint (OMB) for 2025, we propose an integrated approach grounded in iSPAI's decentralized knowledge model, CURT's industry leadership, and modern digital construction best practices. Here's a structured plan to deliver a next-generation OMB that meets the CfS requirements:

✅ Solution Title: Owners Modularization Blueprint 2025 (OMB-2025)

An AI-enabled, blockchain-verified execution framework for modularized capital projects.

📌 Core Objectives

• Codify Modularization Best Practices (from CURT, EPCs, and Owner orgs)
• Integrate Smart Contracts to automate governance and compliance
• Embed Digital Twins for real-time planning, simulation, and safety assurance
• Enable Blockchain-Protected Knowledge Bases (KBs) to retain IP and performance history
• Power AI Agents (e.g., Giblet) for operational planning and workforce augmentation
• Align with iSPAI's Consumption Channel Framework for scalable use (Private, Semi-Private, Public)

🧱 STRUCTURE OF OMB-2025

1. Digital Blueprint Foundation (Knowledge Base Architecture)

Based on the CURT Modularization Blueprint, we will refactor the 14 OMB categories into modular Knowledge Base (KB) components:

• Business Case & Opportunity
• Early Feasibility & Target Value Delivery
• Engineering & Design Integration
• Execution Planning with AWP & Digital Twins
• EHSS and Workforce Planning
• Permitting, Tax, Tariff, and Regulatory Compliance
• Smart Logistics & Transportation
• Quality Assurance & Commissioning

These will be codified as NFT-KBs, with versioning, contributor attribution, and confirmability built-in via smart contracts.

2. Smart Contract Governance

Each KB or Solution will include embedded smart contracts to manage:

• Contribution Roles: Curator, Contributor, Confirmer, Composer
• Milestone-based Payments (e.g., module design, AWP validation, logistics readiness)
• IP Ownership Tracking (e.g., enterprise vs. public vs. workforce-owned KBs)
• Royalty Assignment for future reuse

These contracts will be connected to wallets: iSPAI.eth, iSPAIDAO.eth, and Giblet.eth.

3. AI Integration: Giblet v2 + v3 Deployment

• Giblet v2 will be trained on the modular KBs to assist teams in scenario planning, safety compliance, and resource coordination
• Giblet v3 will connect across multiple KBs to identify cross-project optimization, such as reuse of module designs, logistics strategies, or environmental simulations

4. Digital Twin + Modular Execution Planning

OMB-2025 will include templates and digital twin integration to simulate:

• Fabrication yard productivity
• Labor flows and risk modeling
• Safety inspections and hazard identification
• Module transport readiness
• Onsite/offsite construction alignment

These simulations will be consumed and queried by AI agents and governed by project-level smart contracts.

5. Blockchain Protection & Licensing

Using the iSPAI model, all knowledge will be:

• Tokenized (via OWL + Gib tokens)
• Licensed under a transparent IP structure tied to channel type:
  - Private Channel: Enterprise-owned IP
  - Semi-Private Channel: Shared IP with controlled access
  - Public Channel: Creator-owned IP with marketplace exposure

6. Deployment Phases

• Phase 1 – Convert CURT Modularization Blueprint into modular KBs (Q4 2025)
• Phase 2 – Validate via new CURT pilot capital projects in iSPAI Private/Semi-Private Channels
• Phase 3 – Launch modular KB marketplace in Public Channels with AI-powered consumption tools (Q2 2026)

KPIs for Capital Project Performance

Metric                    | Baseline | OMB-2025 Target
Time to Commission        | -        | ↓ 20-30%
Rework Incidents         | -        | ↓ 25%
EHSS Violations          | -        | ↓ 40%
Knowledge Retention Rate | <10%     | >75%
Modularization ROI       | Varies   | ↑ by 15-25%
AI-Agent Usage (Giblet)  | New      | Daily Ops & Planning

Compliance & Regulation Integration

• Aligns with ISO 45001, OSHA, and cyber-secure AI practices
• Built-in ZK-Proofs for data privacy and trust
• Smart contract governance enforces contribution integrity, safety validation, and licensing

Recommended Action Next:

• Assign Workstream under iSPAI + CURT to begin converting existing Blueprint into KB Modules
• Activate Private and Semi-Private Channels in the iSPAI platform
• Design Smart Contract Templates with CURT and Member Legal/Compliance teams
• Set up Git-style version control for modular KB contributions`
  }
};
interface CommandDemoProps {
  channelName?: string;
}
const CommandDemo: React.FC<CommandDemoProps> = ({
  channelName = "Construction"
}) => {
  const [currentCommandIndex, setCurrentCommandIndex] = useState(0);
  const [displayedDetailedText, setDisplayedDetailedText] = useState('');
  const [phase, setPhase] = useState<'showing' | 'typing' | 'complete'>('showing');
  const [isTypingDetailed, setIsTypingDetailed] = useState(false);
  const currentCommand = constructionCommands[currentCommandIndex];

  // Typewriter effect for detailed response
  useEffect(() => {
    if (phase === 'typing') {
      const currentDetailedResponse = detailedResponses[currentCommand.id];
      if (!currentDetailedResponse) return;
      setIsTypingDetailed(true);
      setDisplayedDetailedText('');
      let i = 0;
      const fullText = currentDetailedResponse.content;
      const typewriterInterval = setInterval(() => {
        if (i < fullText.length) {
          setDisplayedDetailedText(fullText.slice(0, i + 1));
          i++;
        } else {
          setIsTypingDetailed(false);
          setPhase('complete');
          clearInterval(typewriterInterval);
        }
      }, 20); // Faster typing speed: 20ms per character

      return () => clearInterval(typewriterInterval);
    }
  }, [currentCommand.id, phase]);

  // Show phase and start typing
  useEffect(() => {
    if (phase === 'showing') {
      const timer = setTimeout(() => {
        setPhase('typing');
      }, 1000); // Show static content for 1 second before starting typing

      return () => clearTimeout(timer);
    }
  }, [phase]);

  // Complete phase and next command rotation
  useEffect(() => {
    if (phase === 'complete') {
      const timer = setTimeout(() => {
        setCurrentCommandIndex(prev => (prev + 1) % constructionCommands.length);
        setPhase('showing');
      }, 3000); // Pause for 3 seconds before next command

      return () => clearTimeout(timer);
    }
  }, [phase]);
  const getPhaseIcon = () => {
    const IconComponent = currentCommand.icon;
    if (phase === 'typing') {
      return <HardHat className="h-5 w-5 animate-spin text-amber-600" />;
    } else if (phase === 'complete') {
      return <CheckCircle2 className="h-5 w-5 text-green-600" />;
    } else {
      return <IconComponent className="h-5 w-5 text-primary" />;
    }
  };
  const getDisplayContent = () => {
    return <div className="flex items-center gap-3">
        {getPhaseIcon()}
        <span className="text-foreground font-medium py-0 my-[5px]">
          {currentCommand.query}
        </span>
      </div>;
  };
  const currentDetailedResponse = detailedResponses[currentCommand.id];
  
  // For CURT channel, stack both components vertically
  if (channelName === "CURT") {
    return (
      <div className="space-y-6 h-full">
        <Card className="h-auto">
          <CardHeader className="pb-3">
            <h3 className="text-lg font-semibold text-foreground">
              Six Giblet Construct Prompt Examples
            </h3>
          </CardHeader>
          <CardContent className="pt-0 pb-6">
            <div className="flex items-center justify-center gap-4 mb-6">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Link to="/demo-call-for-solutions">
                      <Button className="bg-black text-white hover:bg-[#1e603b] transition-colors">
                        CfS
                      </Button>
                    </Link>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Call for Solution Workflow Demo</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>

              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Link to="/demo-call-for-knowledge">
                      <Button className="bg-black text-white hover:bg-[#1e603b] transition-colors">
                        CfK
                      </Button>
                    </Link>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Call for Knowledge Workflow Demo</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>

              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Link to="/demo-call-for-destruction">
                      <Button className="bg-black text-white hover:bg-[#1e603b] transition-colors">
                        CfD
                      </Button>
                    </Link>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Call for Destruction Workflow Demo</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground mb-4">
              <span className="bg-primary/10 px-2 py-1 rounded-full text-xs">
                {currentCommand.category}
              </span>
              <span className="text-xs">
                {currentCommandIndex + 1} / {constructionCommands.length}
              </span>
            </div>
            
            {/* Navigation Dots */}
            <div className="flex items-center justify-center gap-2 mb-4">
              {constructionCommands.map((_, index) => (
                <button
                  key={index}
                  onClick={() => {
                    setCurrentCommandIndex(index);
                    setPhase('showing');
                  }}
                  className={`w-3 h-3 rounded-full transition-all cursor-pointer ${
                    currentCommandIndex === index
                      ? 'bg-primary scale-125'
                      : 'bg-muted-foreground/30 hover:bg-muted-foreground/50'
                  }`}
                  aria-label={`Go to prompt ${index + 1}`}
                />
              ))}
            </div>
            <div className="flex-1 flex items-center justify-center py-8">
              <div className="border-2 border-primary/20 rounded-lg p-6 bg-background/50 backdrop-blur-sm shadow-sm max-w-2xl w-full">
                {getDisplayContent()}
              </div>
            </div>
          </CardContent>
        </Card>
        
        <DetailedResponsePanel 
          currentDetailedResponse={currentDetailedResponse} 
          currentCommandResponse={currentCommand.response} 
          phase={phase} 
          displayedDetailedText={displayedDetailedText} 
          isTypingDetailed={isTypingDetailed} 
        />
      </div>
    );
  }
  
  // For other channels
  return <>
      {channelName !== "iSPAI" && <Card className="h-full">
          <CardHeader className="pb-3">
            <h3 className="text-lg font-semibold text-foreground">
              Six Giblet Construct Prompt Examples
            </h3>
          </CardHeader>
          <CardContent className="pt-0 pb-6 h-full flex flex-col">
            <div className="flex items-center justify-between mb-6">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <img src={gibletIcon} alt="Giblet Construct" className="h-40 w-auto cursor-pointer" />
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    <p>See Giblet Construct in Action</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <div className="flex items-center gap-4">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Link to="/demo-call-for-solutions">
                        <Button className="bg-black text-white hover:bg-[#1e603b] transition-colors">
                          CfS
                        </Button>
                      </Link>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Call for Solution Workflow Demo</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>

                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Link to="/demo-call-for-knowledge">
                        <Button className="bg-black text-white hover:bg-[#1e603b] transition-colors">
                          CfK
                        </Button>
                      </Link>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Call for Knowledge Workflow Demo</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>

                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Link to="/demo-call-for-destruction">
                        <Button className="bg-black text-white hover:bg-[#1e603b] transition-colors">
                          CfD
                        </Button>
                      </Link>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Call for Destruction Workflow Demo</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <span className="bg-primary/10 px-2 py-1 rounded-full text-xs">
                  {currentCommand.category}
                </span>
                <span className="text-xs">
                  {currentCommandIndex + 1} / {constructionCommands.length}
                </span>
              </div>
            </div>
            
            {/* Navigation Dots */}
            <div className="flex items-center justify-center gap-2 mb-4">
              {constructionCommands.map((_, index) => (
                <button
                  key={index}
                  onClick={() => {
                    setCurrentCommandIndex(index);
                    setPhase('showing');
                  }}
                  className={`w-3 h-3 rounded-full transition-all cursor-pointer ${
                    currentCommandIndex === index
                      ? 'bg-primary scale-125'
                      : 'bg-muted-foreground/30 hover:bg-muted-foreground/50'
                  }`}
                  aria-label={`Go to prompt ${index + 1}`}
                />
              ))}
            </div>
            <div className="flex-1 flex items-center justify-center py-8">
              <div className="border-2 border-primary/20 rounded-lg p-6 bg-background/50 backdrop-blur-sm shadow-sm max-w-2xl w-full">
                {getDisplayContent()}
              </div>
            </div>
          </CardContent>
        </Card>}
      {channelName !== "iSPAI" && <div className="space-y-6">
          <DetailedResponsePanel currentDetailedResponse={currentDetailedResponse} currentCommandResponse={currentCommand.response} phase={phase} displayedDetailedText={displayedDetailedText} isTypingDetailed={isTypingDetailed} />
        </div>}
    </>;
};
export default CommandDemo;